/**
 * TransactionForm Component
 *
 * 거래 입력 폼
 * - 7개 입력 필드 (날짜, 금액, 대분류, 소분류, 결제수단, 결제 계좌 소유자, 메모)
 * - 카테고리 2단계 선택 로직
 * - 결제수단 선택 시 결제 계좌 소유자(spenderId) 자동입력 로직
 * - 유효성 검증
 */

import { useState, useMemo, useEffect, useRef } from 'react'
import type { TransactionInput, TransactionType } from '../types/Transaction'
import { Button } from '@/shared/components/ui/Button'
import { Input } from '@/shared/components/ui/Input'
import { Select } from '@/shared/components/ui/Select'
import { DatePicker } from '@/shared/components/ui/DatePicker'
import { Textarea } from '@/shared/components/ui/Textarea'
import { useCategories } from '@/domains/category/hooks/useCategories'
import { usePaymentMethods } from '@/domains/payment/hooks/usePaymentMethods'
import { useUsers } from '@/domains/user/hooks/useUsers'
import { useCurrentUser } from '@/domains/user/hooks/useCurrentUser'
import { resolveSpenderForPaymentMethod } from '../utils/spenderResolver'

export interface TransactionFormProps {
  initialType?: 'income' | 'expense'
  initialData?: {
    date?: string
    amount?: number
    memo?: string
    majorCategoryId?: string
    minorCategoryId?: string
    paymentMethodId?: string
    spenderId?: string
  }
  onSubmit: (input: TransactionInput) => Promise<void>
  onCancel?: () => void
  submitLabel?: string
  loading?: boolean
}

export function TransactionForm({
  initialType = 'expense',
  initialData,
  onSubmit,
  onCancel,
  submitLabel = '등록',
  loading = false,
}: TransactionFormProps) {
  // 실제 데이터 조회
  const {
    expenseCategories,
    incomeCategories,
    loading: categoriesLoading,
    error: categoriesError,
  } = useCategories()
  const {
    paymentMethods,
    loading: paymentMethodsLoading,
    error: paymentMethodsError,
  } = usePaymentMethods()
  const { users, loading: usersLoading, error: usersError } = useUsers()
  // 수입 "받은 사람" 기본값용 현재 로그인 사용자 (비동기 로딩됨)
  const { currentUser } = useCurrentUser()

  // 상태 관리 (initialData가 있으면 해당 값으로 초기화)
  const [type] = useState<TransactionType>(initialType)
  const [date, setDate] = useState<string>(
    initialData?.date || new Date().toISOString().split('T')[0] || ''
  )
  const [amount, setAmount] = useState<string>(
    initialData?.amount ? String(initialData.amount) : ''
  )
  const [majorCategoryId, setMajorCategoryId] = useState<string>(
    initialData?.majorCategoryId || ''
  )
  const [minorCategoryId, setMinorCategoryId] = useState<string>(
    initialData?.minorCategoryId || ''
  )
  const [paymentMethodId, setPaymentMethodId] = useState<string>(
    initialData?.paymentMethodId || ''
  )
  const [spenderId, setSpenderId] = useState<string>(
    initialData?.spenderId || ''
  )
  const [memo, setMemo] = useState<string>(
    initialData?.memo || ''
  )
  const [errors, setErrors] = useState<Record<string, string>>({})

  // 초기 마운트 감지용 ref (대분류 변경 시 소분류 초기화를 위함)
  const isInitialMount = useRef(true)
  // 수입 "받은 사람" 기본값을 딱 한 번만 채우기 위한 ref (수동 변경 후 재주입 방지)
  const incomeSpenderDefaulted = useRef(false)

  /**
   * initialData 변경 시 상태 재설정
   * (수정 모드에서 다른 거래를 선택할 때 필요)
   */
  useEffect(() => {
    if (initialData) {
      setDate(initialData.date || new Date().toISOString().split('T')[0] || '')
      setAmount(initialData.amount ? String(initialData.amount) : '')
      setMajorCategoryId(initialData.majorCategoryId || '')
      setMinorCategoryId(initialData.minorCategoryId || '')
      setPaymentMethodId(initialData.paymentMethodId || '')
      setSpenderId(initialData.spenderId || '')
      setMemo(initialData.memo || '')
      setErrors({})
    }
  }, [initialData])

  /**
   * 수입 "받은 사람" 기본값 = 현재 로그인 사용자
   *
   * @why 수입은 보통 등록하는 본인이 받으므로 로그인 계정을 기본값으로 채우고,
   *      이후 사용자가 수동 변경 가능하게 둔다.
   *  - 신규 등록(initialData 없음) + 수입 + 아직 미선택일 때만 채운다.
   *  - 수정 모드(initialData 있음)는 저장된 받은 사람을 그대로 보존한다.
   *  - currentUser가 비동기 로딩되므로, 값이 준비된 시점에 채운다.
   *  - ref로 1회만 적용해 사용자가 수동 변경/해제한 뒤 재주입되지 않게 한다.
   */
  useEffect(() => {
    if (initialData) return // 수정 모드: 저장값 유지
    if (type !== 'income') return // 지출은 결제수단(카드) 기반 자동입력 사용
    if (incomeSpenderDefaulted.current) return // 이미 1회 적용됨
    if (!currentUser) return // 로그인 사용자 로딩 대기
    if (spenderId) return // 이미 값이 있으면(수동 선택 등) 유지
    setSpenderId(currentUser.id)
    incomeSpenderDefaulted.current = true
  }, [type, currentUser, initialData, spenderId])

  /**
   * 카테고리 2단계 선택 로직
   */

  // 현재 타입에 맞는 대분류 목록
  const majorCategories = useMemo(() => {
    return type === 'expense' ? expenseCategories : incomeCategories
  }, [type, expenseCategories, incomeCategories])

  // 선택된 대분류의 소분류 목록
  const minorCategories = useMemo(() => {
    const major = majorCategories.find((cat) => cat.id === majorCategoryId)
    return major?.children || []
  }, [majorCategoryId, majorCategories])

  // 대분류 변경 시 소분류 초기화 (초기 마운트는 제외)
  useEffect(() => {
    // 초기 마운트 시에는 실행하지 않음 (initialData로부터 값을 유지하기 위함)
    if (isInitialMount.current) {
      isInitialMount.current = false
      return
    }
    // 사용자가 대분류를 변경한 경우에만 소분류 초기화
    setMinorCategoryId('')
  }, [majorCategoryId])

  /**
   * 결제수단 변경 핸들러
   *
   * "결제 계좌 소유자"(코드상 spenderId)는 결제수단에 종속(카드값이 어느 계좌에서
   * 나가는가)되므로, 결제수단을 고르면 소유자를 자동으로 채운다.
   *  - 비현금: 소유자(ownerId)로 자동입력 (이후 사용자 수동 변경 가능)
   *  - 현금: 빈칸으로 두어 사용자가 직접 선택
   *
   * @why 자동 로직을 useEffect가 아닌 select onChange에 둔다.
   *      - useEffect(paymentMethodId deps)로 두면 수정 모드 초기 로드 시점에도 실행되어
   *        저장된 spenderId를 덮어쓰고, 데이터 로딩 순서에 따라 결과가 불안정해진다.
   *      - onChange는 "사용자가 결제수단을 실제로 바꿀 때"만 실행되므로
   *        수정 모드 초기값 보존 + 로딩 순서 무관 + 수동 선택 후 재덮어쓰기 없음을 모두 만족한다.
   */
  function handlePaymentMethodChange(newPaymentMethodId: string) {
    setPaymentMethodId(newPaymentMethodId)
    // 지출만 결제수단(출금계좌) 소유자로 자동입력한다.
    // 수입의 "받은 사람"은 어느 계좌로 받았는지와 무관(로그인 사용자 기본값)하므로
    // 결제수단을 바꿔도 건드리지 않는다.
    if (type !== 'income') {
      const selected = paymentMethods.find((pm) => pm.id === newPaymentMethodId)
      setSpenderId(resolveSpenderForPaymentMethod(selected))
    }
  }

  /**
   * 유효성 검증
   */
  function validate(): boolean {
    const newErrors: Record<string, string> = {}

    if (!date) newErrors.date = '날짜를 선택해주세요'
    if (!amount || parseFloat(amount) <= 0) newErrors.amount = '금액을 입력해주세요'
    if (!majorCategoryId) newErrors.majorCategoryId = '대분류를 선택해주세요'
    // 소분류는 선택사항으로 변경
    // 결제수단은 "지출"에서만 필수. 수입은 입금 개념이라 결제수단 필드를 숨기고 검증하지 않음
    // (수입의 paymentMethodId는 빈 문자열로 저장됨)
    if (type !== 'income' && !paymentMethodId)
      newErrors.paymentMethodId = '결제수단을 선택해주세요'
    if (!spenderId)
      newErrors.spenderId =
        type === 'income' ? '받은 사람을 선택해주세요' : '결제 계좌 소유자를 선택해주세요'

    setErrors(newErrors)
    return Object.keys(newErrors).length === 0
  }

  /**
   * 폼 제출 핸들러
   */
  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()

    if (!validate()) {
      // 첫 번째 에러 필드로 스크롤
      const firstErrorField = document.querySelector('[data-error="true"]')
      firstErrorField?.scrollIntoView({ behavior: 'smooth', block: 'center' })
      return
    }

    const input: TransactionInput = {
      date,
      type,
      amount: parseFloat(amount),
      majorCategoryId,
      minorCategoryId: minorCategoryId || null,
      paymentMethodId,
      spenderId,
      memo: memo || undefined,
    }

    await onSubmit(input)
  }

  // 로딩 상태 체크
  const isDataLoading = categoriesLoading || paymentMethodsLoading || usersLoading

  // 에러 상태 체크
  const dataError = categoriesError || paymentMethodsError || usersError

  // 소비자 필드(spenderId)의 타입별 표기
  // - 수입: "받은 사람" (기본값 = 로그인 계정)
  // - 지출: "결제 계좌 소유자" (기본값 = 결제수단 소유자)
  const isIncome = type === 'income'
  const spenderLabel = isIncome ? '받은 사람' : '결제 계좌 소유자'

  // 자동입력 안내 문구 (해당 없으면 null → 힌트 미노출)
  // - 수입: 로그인 계정 기본값 안내 (currentUser 있을 때)
  // - 지출: 비현금 결제수단 선택 시에만 안내 (현금은 수동 선택)
  const spenderHint = isIncome
    ? currentUser
      ? '로그인 계정으로 자동 선택됨 · 변경 가능'
      : null
    : paymentMethods.some((pm) => pm.id === paymentMethodId && pm.type !== 'cash')
      ? '카드값이 출금되는 계좌의 소유자 · 결제수단에 따라 자동 선택됨 · 변경 가능'
      : null

  return (
    <form onSubmit={handleSubmit} className="space-y-6">
      {/* 데이터 로딩 중 */}
      {isDataLoading && (
        <div className="text-center py-4 text-gray-500 dark:text-gray-400">
          데이터를 불러오는 중...
        </div>
      )}

      {/* 데이터 로딩 에러 */}
      {dataError && (
        <div className="p-4 bg-red-50 dark:bg-red-900/20 text-red-700 dark:text-red-300 rounded-lg">
          데이터를 불러오는데 실패했습니다: {dataError.message}
        </div>
      )}

      {/* 1. 날짜 */}
      <DatePicker
        label="날짜"
        value={date}
        onChange={setDate}
        required
        error={errors.date}
      />

      {/* 2. 금액 */}
      <Input
        label="금액"
        type="number"
        inputMode="numeric"
        value={amount}
        onChange={setAmount}
        placeholder="0"
        required
        error={errors.amount}
      />

      {/* 3. 대분류 카테고리 */}
      <Select
        label="대분류"
        value={majorCategoryId}
        onChange={setMajorCategoryId}
        options={majorCategories.map((cat) => ({
          value: cat.id,
          label: cat.name,
          icon: cat.icon,
        }))}
        placeholder="대분류 선택"
        required
        error={errors.majorCategoryId}
      />

      {/* 4. 소분류 카테고리 (대분류 선택 후 활성화) */}
      <Select
        label="소분류 (선택사항)"
        value={minorCategoryId}
        onChange={setMinorCategoryId}
        options={minorCategories.map((cat) => ({
          value: cat.id,
          label: cat.name,
          icon: cat.icon,
        }))}
        placeholder="소분류 선택"
        disabled={!majorCategoryId || minorCategories.length === 0}
        error={errors.minorCategoryId}
      />

      {/* 5. 결제수단 (지출 전용 — 수입은 입금 개념이라 숨김, paymentMethodId는 빈값 저장) */}
      {!isIncome && (
        <Select
          label="결제수단"
          value={paymentMethodId}
          onChange={handlePaymentMethodChange}
          options={paymentMethods.map((pm) => ({
            value: pm.id,
            label: pm.name,
          }))}
          placeholder="결제수단 선택"
          required
          error={errors.paymentMethodId}
        />
      )}

      {/* 6. 소비자 필드 (수입=받은 사람 / 지출=결제 계좌 소유자) */}
      <div>
        <Select
          label={spenderLabel}
          value={spenderId}
          onChange={setSpenderId}
          options={users.map((user) => ({
            value: user.id,
            label: user.name,
          }))}
          placeholder={`${spenderLabel} 선택`}
          required
          error={errors.spenderId}
        />
        {/* 자동입력 안내 (수입=로그인 계정 / 지출=비현금 결제수단 소유자) */}
        {spenderHint && (
          <p className="mt-1 text-xs text-gray-500 dark:text-gray-400">{spenderHint}</p>
        )}
      </div>

      {/* 7. 메모 (선택사항) */}
      <Textarea
        label="메모"
        value={memo}
        onChange={setMemo}
        placeholder="거래 메모 (선택사항)"
        rows={3}
        maxLength={200}
      />

      {/* 버튼 영역 */}
      <div className="flex gap-3 pt-4">
        {onCancel && (
          <Button type="button" variant="secondary" onClick={onCancel} fullWidth>
            취소
          </Button>
        )}
        <Button type="submit" variant="primary" loading={loading} fullWidth>
          {submitLabel}
        </Button>
      </div>
    </form>
  )
}
